import React from 'react';

import { SidebarModalSectionComponent } from 'design/sidebarModal';
import { TextComponent } from 'design/text/textComponents';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { usePasswordChangeForm } from 'hooks/usePasswordChangeForm';
import { MarginComponent } from 'design/margin';
import { ModalPressControlComponent } from 'design/modalPressControl';
import { FormControlComponent } from 'design/formControl';
import { PasswordChangeFieldset } from 'containers/PasswordChangeFieldset';
import { useAsyncBoundary } from 'utils/asyncBoundary/asyncBoundaryHooks';
import { FieldsetLayoutComponent } from 'design/fieldsetLayout';
import { validatePasswordChangeFormValues } from 'utils/passwordChangeFormUtils';
import { useServices } from 'hooks/useServices';

type Props = {
  partnerId: number;
  onCancel(): void;
  onEdited(): void;
};

export const PartnerManagerPasswordEditor = (props: Props) => {
  const { partnerId, onCancel: cancel, onEdited } = props;

  const { active: asyncActive, injectPromise } = useAsyncBoundary();

  const { submitPartnerPasswordChangeForm } = useServices();

  const {
    values: formValues,
    violations: formViolations,
    setViolations: setFormViolations,
    setCurrentPassword,
    setNextPassword,
    setNextPasswordRepeat,
  } = usePasswordChangeForm();

  const {
    partnerManager: { passwordEditorScreen: ownDictionary },
  } = useIntlDictionary();

  const submit = React.useCallback(() => {
    setFormViolations([]);

    const violations = validatePasswordChangeFormValues(formValues);

    if (violations.length === 0) {
      const promise = submitPartnerPasswordChangeForm(
        partnerId,
        formValues
      ).then(onEdited);

      injectPromise(promise, { shouldShowFallback: false });
    } else {
      Promise.resolve(violations).then(setFormViolations);
    }
  }, [
    partnerId,
    formValues,
    injectPromise,
    onEdited,
    setFormViolations,
    submitPartnerPasswordChangeForm,
  ]);

  return (
    <>
      <SidebarModalSectionComponent
        theme="head"
        content={
          <TextComponent size="mpp" weight="medium">
            {ownDictionary.titleText}
          </TextComponent>
        }
        actions={null}
      />
      <FormControlComponent
        shouldFocusFirstInvalidInput={formViolations.length > 0}
        onSubmit={submit}
      >
        <SidebarModalSectionComponent
          content={
            <FieldsetLayoutComponent>
              <PasswordChangeFieldset
                disabled={asyncActive}
                values={formValues}
                violations={formViolations}
                onCurrentPasswordChange={setCurrentPassword}
                onNextPasswordChange={setNextPassword}
                onNextPasswordRepeatChange={setNextPasswordRepeat}
              />
            </FieldsetLayoutComponent>
          }
          actions={
            <>
              <ModalPressControlComponent theme="primary" type="submit">
                {ownDictionary.submitActionText}
              </ModalPressControlComponent>
              <MarginComponent top={20} />
              <ModalPressControlComponent
                theme="secondary"
                type="button"
                onPress={cancel}
              >
                {ownDictionary.cancelActionText}
              </ModalPressControlComponent>
            </>
          }
        />
      </FormControlComponent>
    </>
  );
};
